import {
  Hct,
  argbFromHex,
  hexFromArgb,
} from "@material/material-color-utilities";

import { generateMaterialYouScheme } from "../materialTheme";

import {
  COLOUR_ROLES,
  ColourRole,
  LAYOUT_KEYS,
  NEUTRAL_ROLES,
  NEUTRAL_VARIANT_ROLES,
  PRESENCE_KEYS,
  RADIUS_KEYS,
  SHADOW_KEYS,
  SPACE_KEYS,
} from "./keys";
import type { ThemeDefinition } from "./types";

export type TypusTokens = Record<`--typ-${string}`, string>;

/**
 * Convert a hex colour to an "R, G, B" triplet
 * @param hex Colour in #RRGGBB format
 * @returns Triplet string
 */
export function hexToTriplet(hex: string) {
  const [, r, g, b] = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/i.exec(hex)!;
  return `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`;
}

/**
 * Generate the `--typ-*` design tokens for a theme
 * @param definition Theme definition
 * @returns CSS variables
 */
export function createTypusTokens(definition: ThemeDefinition): TypusTokens {
  const tokens: TypusTokens = {};
  const { colors, typography, spacing } = definition;

  // colours
  const palette = generateMaterialYouScheme(
    colors.source,
    definition.darkMode,
    colors.contrast,
    colors.variant,
  );

  // tertiary roles follow the accent colour when one is given
  if (colors.accent) {
    const accent = generateMaterialYouScheme(
      colors.accent,
      definition.darkMode,
      colors.contrast,
      colors.variant,
    );

    for (const role of COLOUR_ROLES) {
      if (role.includes("tertiary")) {
        palette[role] =
          accent[role.replace("tertiary", "primary") as ColourRole];
      }
    }
  }

  // tint neutral surfaces with the source hue, keeping each role's tone
  if (colors.surfaceChroma) {
    const hue = Hct.fromInt(argbFromHex(colors.source)).hue;
    const tint = (role: ColourRole, chroma: number) => {
      const tone = Hct.fromInt(argbFromHex(palette[role])).tone;
      palette[role] = hexFromArgb(Hct.from(hue, chroma, tone).toInt());
    };

    for (const role of NEUTRAL_ROLES) tint(role, colors.surfaceChroma);
    for (const role of NEUTRAL_VARIANT_ROLES)
      tint(role, colors.surfaceChroma * 1.5);
  }

  for (const role of COLOUR_ROLES) {
    tokens[`--typ-color-${role}`] = palette[role];
    tokens[`--typ-rgb-${role}`] = hexToTriplet(palette[role]);
  }

  for (const key of PRESENCE_KEYS) {
    tokens[`--typ-color-presence-${key}`] = colors.presence[key];
  }

  // gradients & backgrounds
  for (const [key, value] of Object.entries(definition.gradients)) {
    tokens[`--typ-gradient-${key}`] = value;
  }

  for (const [key, value] of Object.entries(definition.backgrounds)) {
    tokens[`--typ-background-${key}`] = value;
  }

  // typography
  tokens["--typ-font-ui"] = `"${typography.ui}", "Inter", sans-serif`;
  tokens["--typ-font-mono"] =
    `"${typography.mono}", "Jetbrains Mono", sans-serif`;
  tokens["--typ-message-size"] = `${typography.messageSize}px`;
  tokens["--typ-tracking"] = typography.tracking;
  tokens["--typ-emoji-size"] = typography.emoji.inline;
  tokens["--typ-emoji-size-medium"] = typography.emoji.medium;
  tokens["--typ-emoji-size-large"] = typography.emoji.large;

  // shape
  for (const key of RADIUS_KEYS) {
    tokens[`--typ-radius-${key}`] = definition.radius[key];
  }

  tokens["--typ-border-width"] = definition.borders.width;

  for (const key of SHADOW_KEYS) {
    tokens[`--typ-shadow-${key}`] = definition.shadows[key];
  }

  // spacing
  for (const key of SPACE_KEYS) {
    tokens[`--typ-space-${key}`] = spacing.scale[key];
  }

  for (const key of LAYOUT_KEYS) {
    tokens[`--typ-layout-${key}`] = spacing.layout[key];
  }

  tokens["--typ-message-group-spacing"] = `${spacing.messageGroup}px`;

  // motion
  tokens["--typ-motion-fast"] = definition.motion.fast;
  tokens["--typ-motion-medium"] = definition.motion.medium;
  tokens["--typ-motion-scale"] = `${definition.motion.scale}`;

  // effects
  tokens["--typ-effect-blur"] = definition.effects.blur
    ? "blur(20px)"
    : "unset";
  tokens["--typ-effect-invert-black"] = definition.darkMode
    ? "invert(100%)"
    : "invert(0%)";
  tokens["--typ-effect-invert-light"] = definition.darkMode
    ? "invert(0%)"
    : "invert(1000%)";
  // app shell: plain surfaces, or a wallpaper behind translucent panels
  const shell = {
    app: "surface-container-high",
    sidebar: "surface-container-low",
    content: "surface-container-low",
    main: "surface-container-lowest",
  } as const;

  const { wallpaper } = definition;
  if (wallpaper) {
    const dim = wallpaper.dim / 100;
    const alpha = wallpaper.panelOpacity / 100;

    tokens["--typ-background-app"] =
      `linear-gradient(rgba(0, 0, 0, ${dim}), rgba(0, 0, 0, ${dim})), url("${wallpaper.url}") center / cover no-repeat fixed, var(--typ-color-${shell.app})`;
    tokens["--typ-c-shell-app"] = "var(--typ-background-app)";

    for (const key of ["sidebar", "content"] as const) {
      tokens[`--typ-c-shell-${key}`] =
        `rgba(var(--typ-rgb-${shell[key]}), ${alpha})`;
    }

    // the main panel sits on top of the content panel, halve its opacity
    // so the combined result stays close to the chosen value
    tokens["--typ-c-shell-main"] =
      `rgba(var(--typ-rgb-${shell.main}), ${alpha / 2})`;

    tokens["--typ-c-shell-backdrop"] =
      wallpaper.blur > 0 ? `blur(${wallpaper.blur}px)` : "none";

    // sections inside the sidebar would stack opacity, let the sidebar show
    tokens["--typ-c-shell-section"] = "transparent";
  } else {
    for (const key of ["app", "sidebar", "content", "main"] as const) {
      tokens[`--typ-c-shell-${key}`] = `var(--typ-color-${shell[key]})`;
    }

    tokens["--typ-c-shell-backdrop"] = "none";
    tokens["--typ-c-shell-section"] = `var(--typ-color-${shell.sidebar})`;
  }

  // subtle glow keeps the dark logo outline visible on dark surfaces
  tokens["--typ-effect-logo"] = definition.darkMode
    ? "drop-shadow(0 0 1.5px var(--typ-color-primary))"
    : "none";

  // component overrides
  for (const [component, props] of Object.entries(definition.components)) {
    for (const [prop, value] of Object.entries(props)) {
      tokens[`--typ-c-${component}-${prop}`] = value;
    }
  }

  return tokens;
}
